import { useState, useRef, useEffect } from 'react'
import { fetchAutocompleteOptions, type AutocompleteItem } from '../utils/api'

export interface Insurer {
  name: string
  address?: string
  nif?: string
}

interface InsurerAutocompleteProps {
  value: string
  onChange: (value: string) => void
  onInsurerSelect?: (insurer: Insurer) => void
  placeholder?: string
  required?: boolean
  id?: string
}

export function InsurerAutocomplete({
  value,
  onChange,
  onInsurerSelect,
  placeholder,
  required,
  id,
}: InsurerAutocompleteProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [allOptions, setAllOptions] = useState<AutocompleteItem[]>([])
  const [filteredOptions, setFilteredOptions] = useState<AutocompleteItem[]>([])
  const wrapperRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  // Fetch options on mount
  useEffect(() => {
    fetchAutocompleteOptions('seguradora').then(setAllOptions)
  }, [])

  // Filter options based on input
  useEffect(() => {
    if (value.trim()) {
      const filtered = allOptions.filter((option) =>
        option.name.toLowerCase().includes(value.toLowerCase())
      )
      setFilteredOptions(filtered)
    } else {
      setFilteredOptions(allOptions)
    }
  }, [value, allOptions])

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleSelect = (option: AutocompleteItem) => {
    onChange(option.name)
    onInsurerSelect?.({
      name: option.name,
      address: option.address,
      nif: option.nif,
    })
    setIsOpen(false)
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange(e.target.value)
    setIsOpen(true)
  }

  const handleFocus = () => {
    setIsOpen(true)
  }

  return (
    <div ref={wrapperRef} className="relative">
      <input
        ref={inputRef}
        type="text"
        id={id}
        value={value}
        onChange={handleInputChange}
        onFocus={handleFocus}
        className="input"
        placeholder={placeholder}
        required={required}
        autoComplete="off"
      />
      
      {isOpen && filteredOptions.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-xl shadow-lg max-h-60 overflow-y-auto">
          {filteredOptions.map((option) => (
            <button
              key={option.id}
              type="button"
              onClick={() => handleSelect(option)}
              className="w-full px-4 py-3 text-left hover:bg-gray-50 border-b border-gray-100 last:border-b-0 transition-colors"
            >
              <div className="font-medium text-gray-900">{option.name}</div>
              {option.address && (
                <div className="text-xs text-gray-500 mt-0.5">{option.address}</div>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
